import { Checkbox, Heading } from '@mm/components';
import { ExportOutlinedIcon } from '@mm/icons';
import ChatbotModel, { ChannelName } from '@mm/state/model/ChatbotModel';
import { selectActiveFacebookPage } from '@mm/state/selectors/facebookPageSelector';
import { hasConnectedRealFacebookPageSelector } from '@mm/state/selectors/userSelector';
import { activeChatbotSelector } from '@mm/state/selectors/viewSelectors';
import classNames from 'classnames';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  ButtonColor,
  PrimaryButton,
} from '../../../../../common/buttons/PrimaryButton/PrimaryButton';
import { UserRoleContainer } from '../../../../../common/hooks/use-route-guard';
import { useChannelConnection } from '../../../beta/modules/Automations/AutomationEdit/InputTriggers/use-channel-connection';
import { ChannelIcon } from '../../../liveChat/components/ChannelIcon';
import './ChatbotChannelsCheckmarks.pcss';

type Props = {
  css?: {
    container?: string;
    channel?: string;
  };
  className?: string;
  onToggle: (e: React.ChangeEvent<HTMLInputElement>) => void;
  isChecked: (name: ChannelName) => boolean;
  disabled?: boolean;
};

export const ChatbotChannelsCheckmarks = ({
  css,
  disabled,
  onToggle,
  isChecked,
}: Props) => {
  const {
    roleFlags: { isLimitedRole },
  } = UserRoleContainer.useContainer();

  const chatbot = useSelector(activeChatbotSelector);
  const connectedPage = useSelector(selectActiveFacebookPage);

  const { id, template } = chatbot || ({} as ChatbotModel);
  const isBotActive = connectedPage?.activeBotId === id && !template;

  const isUserPageConnected = useSelector(hasConnectedRealFacebookPageSelector);

  const { connectChannel } = useChannelConnection(() => {});

  const {
    smsActivated,
    instagramActivated,
    webChatActivated,
    emailActivated,
    realFacebookPage,
    remoteId,
    name: fbName,
    availableInstagramAccountsData,
  } = connectedPage || {};

  const { username, name: igName } = availableInstagramAccountsData || {};

  const notAllowed = useMemo(() => {
    return !isBotActive || disabled || isLimitedRole;
  }, [isBotActive, disabled, isLimitedRole]);

  const atLeastOneActive = [
    isUserPageConnected,
    instagramActivated,
    smsActivated,
    webChatActivated,
    emailActivated,
  ].some(Boolean);

  return (
    <div
      className={classNames(css?.container, 'chatbot-channels__container', {
        'chatbot-channels__channels--disabled': !isBotActive,
      })}
    >
      {isUserPageConnected ? (
        <div className={classNames(css?.channel, 'chatbot-channels__channel')}>
          <Checkbox
            disabled={notAllowed}
            isChecked={isChecked('facebook')}
            name="facebook"
            onToggle={onToggle}
          />
          <ChannelIcon channel="facebook" />
          <Heading size={4} isBold={false}>
            {realFacebookPage && fbName ? fbName : 'Messenger'}
          </Heading>
          {realFacebookPage ? (
            <ExportOutlinedIcon
              className="chatbot-status-section__export-icon"
              onClick={() =>
                window.open(`https://www.facebook.com/${remoteId}`, '_blank')
              }
            />
          ) : null}
        </div>
      ) : (
        <div
          className={classNames(
            css?.channel,
            'chatbot-channels__channel-not-connected',
          )}
        >
          <ChannelIcon channel="facebook" />
          <PrimaryButton
            color={ButtonColor.secondaryBlue}
            onClick={() => connectChannel('facebook')}
          >
            Connect Facebook
          </PrimaryButton>
        </div>
      )}

      {instagramActivated ? (
        <div className={classNames(css?.channel, 'chatbot-channels__channel')}>
          <Checkbox
            disabled={notAllowed}
            isChecked={isChecked('instagram')}
            name="instagram"
            onToggle={onToggle}
          />
          <ChannelIcon channel="instagram" />
          <Heading size={4} isBold={false}>
            {igName ? `@${igName}` : 'Instagram'}
          </Heading>
          {username ? (
            <ExportOutlinedIcon
              className="chatbot-status-section__export-icon"
              onClick={() =>
                window.open(`https://www.instagram.com/${username}`, '_blank')
              }
            />
          ) : null}
        </div>
      ) : (
        <div
          className={classNames(
            css?.channel,
            'chatbot-channels__channel-not-connected',
          )}
        >
          <ChannelIcon channel="instagram" />
          <PrimaryButton
            color={ButtonColor.secondaryBlue}
            onClick={() => connectChannel('instagram')}
          >
            Connect Instagram
          </PrimaryButton>
        </div>
      )}

      {smsActivated && (
        <div className={classNames(css?.channel, 'chatbot-channels__channel')}>
          <Checkbox
            disabled={notAllowed}
            isChecked={isChecked('sms')}
            name="sms"
            onToggle={onToggle}
          />
          <ChannelIcon channel="sms" />
          <Heading size={4} isBold={false}>
            SMS
          </Heading>
        </div>
      )}

      {webChatActivated && (
        <div className={classNames(css?.channel, 'chatbot-channels__channel')}>
          <Checkbox
            disabled={notAllowed}
            isChecked={isChecked('webChat')}
            name="webChat"
            onToggle={onToggle}
          />
          <ChannelIcon channel="webchat" />
          <Heading size={4} isBold={false}>
            Webchat
          </Heading>
        </div>
      )}

      {emailActivated && (
        <div className={classNames(css?.channel, 'chatbot-channels__channel')}>
          <Checkbox
            disabled={notAllowed}
            isChecked={isChecked('email')}
            name="email"
            onToggle={onToggle}
          />
          <ChannelIcon channel="email" />
          <Heading size={4} isBold={false}>
            Email
          </Heading>
        </div>
      )}

      {!atLeastOneActive && (
        <span className="chatbot-channels__empty">
          You don't have any active channels yet. Please create Chat Starters in
          order to activate one
        </span>
      )}
    </div>
  );
};
